import { motion, useReducedMotion } from "framer-motion";
import RaceCar from "./brand/RaceCar";

export interface Lane {
  id: string;
  label: string;
  fraction: number; // 0..1 progress to finish
  carColor: string;
  number: string | number;
  isYou?: boolean;
  isGhost?: boolean;
  /** Render a character token (e.g. 🐰) instead of a race car. */
  emoji?: string;
  finished?: boolean;
}

export default function RaceTrack({ lanes }: { lanes: Lane[] }) {
  const reduce = useReducedMotion();
  return (
    <div className="panel p-3 sm:p-4" aria-label="Race track" role="group">
      <div className="flex flex-col gap-3">
        {lanes.map((lane) => (
          <div
            key={lane.id}
            role="group"
            aria-label={`${lane.label}: ${Math.round(lane.fraction * 100)}% to the finish${
              lane.finished ? ", finished" : ""
            }`}
          >
            <div
              className="mb-1 flex items-center justify-between text-xs font-bold"
              aria-hidden
            >
              <span className={lane.isYou ? "text-flame" : "text-ink/50"}>
                {lane.isYou ? "🏎️ " : lane.emoji ? `${lane.emoji} ` : ""}
                {lane.label}
              </span>
              <span className="tabular-nums text-ink/40">
                {Math.round(lane.fraction * 100)}%
              </span>
            </div>
            <div className="relative h-14 overflow-hidden rounded-2xl border-[4px] border-road-edge bg-road-surface">
              {/* checkered curb top */}
              <div
                className="absolute inset-x-0 top-0 h-2"
                style={{
                  background:
                    "repeating-conic-gradient(#1c2a63 0% 25%, #fff 0% 50%) 0 0/16px 16px",
                  opacity: 0.85,
                }}
                aria-hidden
              />
              {/* dashed centre line */}
              <div
                className="pointer-events-none absolute inset-x-2 top-1/2 h-1.5 -translate-y-1/2 rounded"
                style={{
                  background:
                    "repeating-linear-gradient(90deg, #ffd23f 0 28px, transparent 28px 56px)",
                  opacity: lane.isGhost ? 0.3 : 0.7,
                }}
                aria-hidden
              />
              {/* finish line */}
              <div
                className="absolute inset-y-0 right-1.5 w-2.5 rounded"
                style={{
                  background:
                    "repeating-conic-gradient(#fff 0% 25%, #1c2a63 0% 50%) 0 0/10px 10px",
                }}
                aria-hidden
              />
              {/* racer token */}
              <motion.div
                className="absolute top-1/2 -translate-y-1/2"
                style={{ width: 66 }}
                initial={false}
                animate={{ left: `calc((100% - 74px) * ${Math.min(lane.fraction, 1)} + 4px)` }}
                transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 180, damping: 22 }}
                aria-hidden
              >
                {lane.emoji ? (
                  <div
                    className={`grid h-12 w-[66px] place-items-center text-[34px] leading-none ${
                      reduce ? "" : "animate-car-bob"
                    }`}
                    style={{ transformOrigin: "center bottom" }}
                  >
                    {lane.emoji}
                  </div>
                ) : (
                  <div className={lane.isGhost ? "opacity-50 grayscale" : ""}>
                    <RaceCar
                      width={66}
                      color={lane.carColor}
                      number={lane.number}
                      spinning={!lane.isGhost}
                      ariaLabel={lane.label}
                    />
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
